import React, { useRef, useState } from "react";
import { Form, Input, Select, Button, Checkbox, Row, Col } from "antd";
import { InputMask } from "@mtm/components";
import { selectDropdownArrowIcon } from "../../../assets/images";
import { PersonInitialValues, PersonType } from "../../../types/PersonTypes";
import PersonAddress from "./PersonAddress";
import styles from "./PersonForm.module.scss";
import PersonPhoto from "./PersonPhoto";
import PersonAccommodations from "./PersonAccommodations";
import PersonDateOfBirth from "./PersonDateOfBirth";
import { usePersonForm } from "../../../hooks/usePersonForm";
import { isEmailValid } from "../../../helpers/ValidationHelpers";
import BenefitsModal from "../Modals/BenefitsModal";
import DuplicateServiceUserModal from "../Modals/DuplicateServiceUserModal";
import { usePersonCreation } from "../../../hooks/usePersonCreation";
import { usePersonSummaryCtx } from "../../../context/PersonSummaryContext";
import {
  ServicesTypesComponentLoad,
  TrackAnalytics,
} from "../../../types/ApplicationTypes";
import { useServiceUserCreationCtx } from "../../../context/ServiceUserCreationContext";
import { defaultSelectedAddress } from "../../../types/LocationTypes";
import UspsAddressModal from "../Modals/UspsAddressModal";
import { useUspsValidator } from "../../../hooks/useUspsValidator";

type Props = {
  personType: PersonType.SomeoneElse | PersonType.Myself;
  initialValues?: PersonInitialValues;
};

function PersonForm({ personType, initialValues }: Props) {
  const [
    isDuplicateUserModalVisible,
    setIsDuplicateUserModalVisible,
  ] = useState<boolean>(false);
  const [isBenefitsModalVisible, setIsBenefitsModalVisible] = useState<boolean>(
    false
  );
  const isDuplicateUserUnderSameCaregiver = useRef<boolean>(false);
  const { caregiverSummaryInfo } = usePersonSummaryCtx();
  const {
    pageTypeCtx,
    relationshipTypesCtx,
    registrationBackBtnClickCtx,
    isLoadingCtx,
  } = useServiceUserCreationCtx();
  const [, setIsBackBtnClick] = registrationBackBtnClickCtx;
  const [, setPageType] = pageTypeCtx;
  const [isLoading, setIsLoading] = isLoadingCtx;
  const [relationshipTypes] = relationshipTypesCtx;

  const {
    isPersonMatchFound,
    getMatchedPersonRelCheck,
    getMemberStatus,
    handlePersonCreationData,
  } = usePersonCreation();

  const [form] = Form.useForm();

  const {
    labels,
    shouldDisableBtn,
    getFormFieldValues,
    validateAndCheckForm,
    isAddCaregiverAsServiceUser,
  } = usePersonForm(form, personType);

  const {
    addressRef,
    uspsAddresses,
    handleUspsValidationAndSubmitForm,
    isUspsAddressModalVisible,
    setIsUspsAddressModalVisible,
  } = useUspsValidator(form, handleSubmitForm);

  function resetForm() {
    form.resetFields();
    addressRef?.current?.forEach((ref) =>
      ref.current?.handleResetAddressLookup()
    );
  }

  function handleIsSameAsHomeAddress(isChecked: boolean) {
    if (isChecked) {
      const address = form.getFieldValue("homeAddress");
      form.setFieldsValue({
        mailingAddress: {
          ...address,
        },
      });
    } else {
      form.setFieldsValue({
        mailingAddress: defaultSelectedAddress,
      });
    }
    validateAndCheckForm();
  }

  const handleValuesChange = () => validateAndCheckForm();

  async function handleSubmitForm() {
    setIsLoading(true);
    const values = getFormFieldValues();
    if (personType === PersonType.Myself) {
      TrackAnalytics(
        "/Welcome",
        "Myself Page - Continue Button Clicked",
        "Myself Page"
      );
      await handlePersonCreationData(
        values,
        personType,
        caregiverSummaryInfo.personId,
        false
      );
      setPageType(ServicesTypesComponentLoad.TrustedContacts);
    } else {
      TrackAnalytics(
        "/Welcome",
        "Someone Else Page - Continue Button Clicked",
        "Someone Else Page"
      );
      const personMatch = await isPersonMatchFound(values);
      if (personMatch.success === true) {
        const serviceUserInfo = await getMatchedPersonRelCheck(
          personMatch.personId
        );
        if (serviceUserInfo.length === 0) {
          await handlePersonCreationData(
            values,
            personType,
            personMatch.personId,
            true
          );
          const isMemberStatus = await getMemberStatus(personMatch.personId);
          if (isMemberStatus === true) {
            setIsBenefitsModalVisible(true);
          } else {
            setPageType(ServicesTypesComponentLoad.TrustedContacts);
          }
        } else {
          if (serviceUserInfo[0].personId === caregiverSummaryInfo.personId) {
            //Service user managed by same caregiver
            isDuplicateUserUnderSameCaregiver.current = true;
          } else {
            //Service user managed by a different caregiver
            isDuplicateUserUnderSameCaregiver.current = false;
          }
          setIsDuplicateUserModalVisible(true);
        }
      } else {
        await handlePersonCreationData(values, personType, 0, false);
        setPageType(ServicesTypesComponentLoad.TrustedContacts);
      }
    }
    setIsLoading(false);
    setIsBackBtnClick(false);
  }

  const handleBenefitsRouting = () => {
    setPageType(ServicesTypesComponentLoad.TrustedContacts);
  };

  return (
    <>
      <Form
        form={form}
        name={`${personType}Form`}
        layout="vertical"
        initialValues={initialValues}
        onValuesChange={handleValuesChange}
        onFinish={handleUspsValidationAndSubmitForm}
        requiredMark={false}
        validateTrigger={["onBlur"]}
      >
        <Row>
          <Col span={24} className={styles.title}>
            {labels.title}
          </Col>

          <Col xs={24} lg={12}>
            <Row className={styles.mobileFormAlign}>
              <Col span={24} className={styles.label}>
                {labels.basicInfoLabel}
              </Col>

              <Col span={24}>
                <Form.Item name="firstName">
                  <Input
                    maxLength={100}
                    placeholder="First Name"
                    className={`${styles.input} ${isAddCaregiverAsServiceUser &&
                      styles.disabledInput}`}
                    disabled={isAddCaregiverAsServiceUser}
                  />
                </Form.Item>
              </Col>

              <Col span={24}>
                <Form.Item name="lastName">
                  <Input
                    maxLength={100}
                    placeholder="Last Name"
                    className={`${styles.input} ${isAddCaregiverAsServiceUser &&
                      styles.disabledInput}`}
                    disabled={isAddCaregiverAsServiceUser}
                  />
                </Form.Item>
              </Col>

              <Col span={24}>
                <Form.Item name="phone">
                  <InputMask
                    className={`${styles.input} ${isAddCaregiverAsServiceUser &&
                      styles.disabledInput}`}
                    mask="999-999-9999"
                    maskPlaceholder=""
                    placeholder="Phone Number"
                    disabled={isAddCaregiverAsServiceUser}
                    onChange={() => {}}
                  />
                </Form.Item>
              </Col>

              <Col span={24}>
                <Form.Item
                  name="email"
                  rules={[
                    { required: false, message: "" },
                    {
                      validator(_, value) {
                        if (!value || isEmailValid(value)) {
                          return Promise.resolve();
                        }
                        return Promise.reject("");
                      },
                    },
                  ]}
                  className={`${!isAddCaregiverAsServiceUser &&
                    styles.formItemMargin}`}
                >
                  <Input
                    placeholder="Email"
                    className={`${styles.input} ${isAddCaregiverAsServiceUser &&
                      styles.disabledInput}`}
                    disabled={isAddCaregiverAsServiceUser}
                  />
                </Form.Item>
                {!isAddCaregiverAsServiceUser && (
                  <Row gutter={[0, 10]}>
                    <Col className={styles.optional}>**Optional</Col>
                    <Col span={24} />
                  </Row>
                )}
              </Col>

              {!isAddCaregiverAsServiceUser && (
                <Col span={24}>
                  <Form.Item name="relationship">
                    <Select
                      className={styles.relationshipSelect}
                      placeholder="Relationship"
                      suffixIcon={
                        <img
                          src={selectDropdownArrowIcon}
                          alt="select_reason_dropdown"
                        />
                      }
                      options={relationshipTypes}
                    />
                  </Form.Item>
                </Col>
              )}

              <Col span={24}>
                <PersonDateOfBirth
                  form={form}
                  isEditProfile={false}
                  labels={labels}
                />
              </Col>
            </Row>
          </Col>

          <Col xs={24} lg={12}>
            <Row className={styles.mobileFormAlign}>
              <Col span={24} className={styles.label}>
                {labels.address.homeLabel}
              </Col>
              <Col>
                <PersonAddress
                  ref={addressRef.current[0]}
                  form={form}
                  labels={labels.address}
                  addressType="homeAddress"
                  validateAndCheckForm={validateAndCheckForm}
                  shouldValidateZip={true}
                />
              </Col>

              <Col span={24} className={styles.label}>
                {labels.address.mailingLabel}
              </Col>
              <Col span={24}>
                <Row gutter={[10, 10]} align="middle">
                  <Col>
                    <Form.Item
                      name="isSameAsHomeAddress"
                      className={styles.formItemMargin}
                      valuePropName="checked"
                    >
                      <Checkbox
                        className={styles.checkbox}
                        onChange={(e) =>
                          handleIsSameAsHomeAddress(e.target.checked)
                        }
                      />
                    </Form.Item>
                  </Col>
                  <Col className={styles.checkboxLabel}>
                    Same as home address
                  </Col>
                  <Col span={24} />
                </Row>
              </Col>

              <Col span={24}>
                <Form.Item
                  className={styles.formItemMargin}
                  dependencies={["isSameAsHomeAddress"]}
                >
                  {({ getFieldValue }) =>
                    !getFieldValue("isSameAsHomeAddress") && (
                      <PersonAddress
                        ref={addressRef.current[1]}
                        form={form}
                        labels={labels.address}
                        addressType="mailingAddress"
                        validateAndCheckForm={validateAndCheckForm}
                        shouldValidateZip={true}
                      />
                    )
                  }
                </Form.Item>
              </Col>

              <Col span={24}>
                <Row gutter={[5, 0]}>
                  <Col className={styles.label}>Upload a photo?</Col>{" "}
                  <Col className={styles.optional}>**Optional</Col>
                </Row>

                <PersonPhoto form={form} />
              </Col>

              <Col span={24} className={styles.label}>
                Accommodations
              </Col>
              <Col span={24}>
                <PersonAccommodations
                  form={form}
                  labels={labels.accommodations}
                  accommodationType="wandaAccommodations"
                />
              </Col>

              <Col span={24} className={styles.mobileContinueBtnAlign}>
                <Button
                  className={`${styles.continueBtn} ${shouldDisableBtn &&
                    styles.disableBtn}`}
                  type="primary"
                  htmlType="submit"
                  disabled={shouldDisableBtn}
                  loading={isLoading}
                >
                  Continue
                </Button>
              </Col>
            </Row>
          </Col>
        </Row>
      </Form>

      <DuplicateServiceUserModal
        form={form}
        visible={isDuplicateUserModalVisible}
        setVisibility={setIsDuplicateUserModalVisible}
        resetForm={resetForm}
        isDuplicateUserUnderSameCaregiver={
          isDuplicateUserUnderSameCaregiver.current
        }
      />

      <BenefitsModal
        form={form}
        visible={isBenefitsModalVisible}
        setVisibility={setIsBenefitsModalVisible}
        handleRouting={handleBenefitsRouting}
      />

      <UspsAddressModal
        form={form}
        uspsAddresses={uspsAddresses.current}
        visible={isUspsAddressModalVisible}
        setVisibility={setIsUspsAddressModalVisible}
        handleSubmitForm={handleSubmitForm}
      />
    </>
  );
}

export default PersonForm;
